import React, { useCallback, useState, useRef } from "react";
import { Form, InputNumber, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import FilterBar from "../../components/FilterBar";
import { useConfirm } from "../../hooks/useConfirm";
import { useMutations } from "../../hooks/useApiMutation";

const TokenValuesList = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, _search, f) => {
      let result = { data: [], total: 0 };

      await mutate("fetchTokenValues", {
        method: "get",
        endpoint: "/tokenvalues",
        params: {
          page,
          pageSize,
          createdDate: f?.createdDate
            ? f.createdDate.toISOString()
            : undefined,
        },
        onSuccess: (res) => {
          result = {
            data: res?.items || [],
            total: res?.totalCount || 0,
          };
        },
        onError: () => {
          result = { data: [], total: 0 };
        },
      });

      return result;
    },
    [mutate]
  );

  const columns = [
    {
      title: "Euro Per Token",
      dataIndex: "euroPerToken",
      key: "euroPerToken",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => (d ? new Date(d).toLocaleDateString() : ""),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Edit"><Button variant="ghost" icon={<EditOutlined />} onClick={() => onEdit(record)} /></Tooltip>
          <Tooltip title="Delete"><Button
            variant="ghost"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              confirmAction({
                key: "deleteTokenValue",
                method: "delete",
                endpoint: `/tokenvalues/${record.id}`,
                title: "Delete Confirmation",
                content: "Are you sure you want to delete this item?",
                okText: "Delete",
                okType: "danger",
                onSuccess: () => {
                  tableRef.current?.reloadPage();
                },
              })
            }
          /></Tooltip>
        </>
      ),
    },
  ];

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    confirmAction({
      key: editing ? "updateTokenValue" : "createTokenValue",
      method: editing ? "put" : "post",
      endpoint: editing ? `/tokenvalues/${editing.id}` : "/tokenvalues",
      payload: values,
      title: editing ? "Update Confirmation" : "Create Confirmation",
      content: "Are you sure you want to submit this item?",
      onSuccess: () => {
        setOpen(false);
        tableRef.current?.reloadPage();
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Token Values"
        subtitle="Per-client token pricing used when metering usage."
        extra={
          <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
            Create Token Value
          </Button>
        }
      />
      <FilterBar
        filters={filters}
        onChange={setFilters}
        showDate
      />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        filters={filters}
        rowKey="id"
      />
      <Modal
        title={editing ? "Edit Token Value" : "Create Token Value"}
        open={open}
        onConfirm={handleOk}
        onClose={() => setOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="euroPerToken"
            label="Euro Per Token"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TokenValuesList;
